/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { causeListsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import { CloudArrowDown, Upload, ListChecks, ArrowRight, Briefcase, Sparkle, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";

export default function CauseLists() {
    const [runs, setRuns] = useState([]);
    const [providers, setProviders] = useState([]);
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null); // currently-open run id

    const load = async () => setRuns(await causeListsApi.list().catch(() => []));

    useEffect(() => {
        load();
        causeListsApi.providers().then(setProviders).catch(() => {});
    }, []);

    if (active) {
        return <CauseListDetail runId={active} onBack={() => { setActive(null); load(); }} />;
    }

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="cause-lists-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Court diary</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Cause lists</h1>
                    <HelpButton topic="cause_lists" testid="help-cause-lists" />
                </div>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-causelist-btn"><CloudArrowDown size={14} /> Fetch / upload</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader>
                            <DialogTitle>New cause-list run</DialogTitle>
                            <DialogDescription>
                                Auto-fetch where available, or upload the PDF you downloaded from the court website. The AI parses items and matches them to your matters.
                            </DialogDescription>
                        </DialogHeader>
                        <RunForm providers={providers} onDone={(r) => { setOpen(false); setActive(r.run_id); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {!runs.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center" data-testid="empty-causelists">
                    <ListChecks size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">No cause-list runs yet</div>
                    <div className="text-sm text-muted-foreground max-w-md mx-auto">
                        Fetch today's list for the Supreme Court or Delhi HC, or upload a PDF for any other court. Items will be auto-matched to your matters.
                    </div>
                </div>
            ) : (
                <div className="border border-border rounded-md overflow-hidden bg-white">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-3">Date</th>
                                <th className="text-left p-3">Court</th>
                                <th className="text-left p-3 hidden md:table-cell">Source</th>
                                <th className="text-left p-3">Items</th>
                                <th className="text-left p-3">Matched</th>
                                <th className="text-left p-3">Status</th>
                                <th className="w-[40px]"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.map((r) => (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/40 cursor-pointer" onClick={() => setActive(r.id)} data-testid={`causelist-row-${r.id}`}>
                                    <td className="p-3 font-medium">{r.list_date}</td>
                                    <td className="p-3">{providers.find((p) => p.key === r.court)?.label || r.court}</td>
                                    <td className="p-3 hidden md:table-cell text-xs text-muted-foreground">{r.source === "fetched" ? "Auto-fetch" : "Manual upload"}</td>
                                    <td className="p-3">{r.stats?.total_items ?? "—"}</td>
                                    <td className="p-3">{r.stats?.matched_matters ? <span className="chip chip-ai">{r.stats.matched_matters}</span> : "—"}</td>
                                    <td className="p-3">
                                        <span className={`chip ${r.status === "ready" ? "chip-ai" : r.status === "error" ? "chip-doc text-destructive" : "chip-doc"}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="p-3 text-muted-foreground"><ArrowRight size={14} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function RunForm({ providers, onDone }) {
    const today = new Date().toISOString().slice(0, 10);
    const [mode, setMode] = useState("fetch");
    const [v, setV] = useState({ court: "supreme_court", list_date: today });
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (mode === "upload" && !file) { toast.error("Pick a PDF first"); return; }
        if (!v.list_date) { toast.error("Date required"); return; }
        setBusy(true);
        try {
            const r = mode === "fetch"
                ? await causeListsApi.fetch(v)
                : await causeListsApi.upload({ ...v, file });
            toast.success("Started — parsing now");
            onDone?.(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
        setBusy(false);
    };

    const supports_fetch = providers.find((p) => p.key === v.court)?.supports_fetch;

    return (
        <div className="space-y-3" data-testid="causelist-form">
            <div className="inline-flex border border-border rounded-sm overflow-hidden">
                <button onClick={() => setMode("fetch")} className={`px-3 h-8 text-xs ${mode === "fetch" ? "bg-primary text-primary-foreground" : "bg-white"}`} data-testid="mode-fetch">
                    Auto-fetch
                </button>
                <button onClick={() => setMode("upload")} className={`px-3 h-8 text-xs border-l border-border ${mode === "upload" ? "bg-primary text-primary-foreground" : "bg-white"}`} data-testid="mode-upload">
                    Manual upload (PDF)
                </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Court</div>
                    <Select value={v.court} onValueChange={(c) => setV({ ...v, court: c })}>
                        <SelectTrigger data-testid="causelist-court"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {providers.map((p) => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">List date</div>
                    <Input type="date" value={v.list_date} onChange={(e) => setV({ ...v, list_date: e.target.value })} data-testid="causelist-date" />
                </div>
            </div>
            {mode === "fetch" && !supports_fetch && (
                <div className="text-xs text-amber-800 bg-amber-50/60 border border-amber-200 rounded-sm p-2" data-testid="fetch-not-supported">
                    Auto-fetch is not currently available for this court. Switch to "Manual upload (PDF)".
                </div>
            )}
            {mode === "upload" && (
                <label className="flex items-center gap-2 border border-dashed border-border rounded-sm p-3 cursor-pointer hover:bg-muted/30">
                    <Upload size={16} className="text-muted-foreground" />
                    <span className="text-sm">{file?.name || "Choose PDF…"}</span>
                    <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={(e) => setFile(e.target.files?.[0])}
                        data-testid="causelist-file"
                    />
                </label>
            )}
            <div className="flex justify-end">
                <Button onClick={submit} disabled={busy || (mode === "fetch" && !supports_fetch)} data-testid="causelist-submit">
                    {busy ? "Starting…" : (mode === "fetch" ? "Fetch & parse" : "Upload & parse")}
                </Button>
            </div>
        </div>
    );
}

function CauseListDetail({ runId, onBack }) {
    const [run, setRun] = useState(null);
    const [matters, setMatters] = useState([]);

    useEffect(() => {
        let alive = true;
        const tick = async () => {
            try {
                const r = await causeListsApi.get(runId);
                if (!alive) return;
                setRun(r);
                if (r.status === "parsing") setTimeout(tick, 3000);
            } catch {
                toast.error("Failed to load run");
            }
        };
        tick();
        import("@/lib/api").then((m) => m.mattersApi.list()).then(setMatters).catch(() => {});
        return () => { alive = false; };
    }, [runId]);

    if (!run) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

    const stages = [
        { key: "fetched", label: run.source === "fetched" ? "Fetched from court site" : "PDF uploaded", status: "completed" },
        { key: "parse", label: "Extracting items via AI", status: run.status === "parsing" ? "processing" : run.status === "ready" ? "completed" : "failed", reasoning: run.status === "error" ? (run.notes || run.error) : (run.status === "ready" ? `${run.stats?.total_items} items parsed.` : "") },
        { key: "match", label: "Matching to your matters", status: run.status === "ready" ? "completed" : run.status === "parsing" ? "queued" : "failed", reasoning: run.status === "ready" ? `${run.stats?.matched_matters} item(s) matched to your matters.` : "" },
    ];

    const updateMatch = async (itemId, matterId) => {
        try {
            await causeListsApi.manualMatch(runId, itemId, { matter_id: matterId || null });
            setRun((r) => ({
                ...r,
                items: r.items.map((it) => it.id === itemId ? { ...it, matched_matter_id: matterId || null, match_reason: matterId ? "manual override" : null, match_confidence: matterId ? 1.0 : null } : it),
                stats: { ...r.stats, matched_matters: r.items.filter((it) => (it.id === itemId ? matterId : it.matched_matter_id)).length },
            }));
        } catch {
            toast.error("Match update failed");
        }
    };
    const createHearing = async (itemId) => {
        try {
            await causeListsApi.createHearing(runId, itemId);
            toast.success("Hearing added to your calendar");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="causelist-detail">
            <button onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-3" data-testid="cl-back">
                <ArrowLeft size={14} /> All cause lists
            </button>
            <div className="rule-line pt-3 mb-4">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">{run.list_date}</div>
                <h1 className="font-serif text-3xl tracking-tight">{matters.length > 0 ? "" : ""}Cause list — {run.court}</h1>
            </div>

            <div className="mb-4">
                <ProgressTimeline stages={stages} title="Run progress" subtitle={`${run.stats?.total_items || 0} items · ${run.stats?.matched_matters || 0} matched`} data-testid="causelist-progress" />
            </div>

            {run.status === "error" && (
                <div className="border border-destructive/40 bg-destructive/5 rounded-sm p-3 text-sm mb-4">
                    <strong>Could not parse this run.</strong>{" "}{run.notes || run.error}
                </div>
            )}

            {run.status === "ready" && (
                <div className="border border-border rounded-md bg-white overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-muted text-muted-foreground">
                            <tr>
                                <th className="text-left p-2">Item</th>
                                <th className="text-left p-2">Case</th>
                                <th className="text-left p-2 hidden lg:table-cell">Bench / Courtroom</th>
                                <th className="text-left p-2 hidden md:table-cell">Stage</th>
                                <th className="text-left p-2">Linked matter</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {run.items.map((it) => (
                                <tr key={it.id} className="border-t border-border" data-testid={`causelist-item-${it.id}`}>
                                    <td className="p-2 font-mono text-xs">{it.item_no}</td>
                                    <td className="p-2">
                                        <div className="font-medium">{it.case_no || it.case_title}</div>
                                        <div className="text-xs text-muted-foreground line-clamp-1">{it.case_title}</div>
                                    </td>
                                    <td className="p-2 hidden lg:table-cell text-xs text-muted-foreground">
                                        {it.bench}
                                        {it.courtroom && <div>Court Room {it.courtroom}</div>}
                                    </td>
                                    <td className="p-2 hidden md:table-cell text-xs">{it.stage}</td>
                                    <td className="p-2 min-w-[200px]">
                                        <Select value={it.matched_matter_id || "none"} onValueChange={(val) => updateMatch(it.id, val === "none" ? null : val)}>
                                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">— Unmatched —</SelectItem>
                                                {matters.map((m) => <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        {it.match_reason && it.matched_matter_id && (
                                            <div className="text-[10px] text-muted-foreground mt-1">
                                                {it.match_reason} {it.match_confidence ? `(${Math.round(it.match_confidence * 100)}%)` : ""}
                                            </div>
                                        )}
                                    </td>
                                    <td className="p-2">
                                        {it.matched_matter_id && (
                                            <Button variant="outline" size="sm" onClick={() => createHearing(it.id)} data-testid={`add-hearing-${it.id}`}>
                                                <Briefcase size={12} /> Add to diary
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
